import { z } from 'zod';
import {
  ROLES,
  PROJECT_STATUS,
  TASK_STATUS,
  CONTACT_TYPES,
  AUDIT_ENTITIES,
  AUDIT_ACTIONS,
} from './enums.js';

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet: YYYY-MM-DD');
const isoTimestamp = z.string().datetime({ offset: true });

export const userSchema = z.object({
  id: z.number().int().positive(),
  abbreviation: z.string().min(1).max(10),
  first_name: z.string().min(1),
  last_name: z.string().min(1),
  email: z.string().email(),
  role: z.enum(ROLES),
  is_active: z.boolean(),
  password_change_required: z.boolean(),
  created_at: isoTimestamp,
  updated_at: isoTimestamp,
});
export type User = z.infer<typeof userSchema>;

export const changePasswordSchema = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(8),
});
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const userCreateSchema = userSchema
  .omit({ id: true, created_at: true, updated_at: true, password_change_required: true })
  .extend({ password: z.string().min(8) });
export type UserCreateInput = z.infer<typeof userCreateSchema>;

export const userUpdateSchema = userCreateSchema.partial().extend({
  password: z.string().min(8).optional(),
});
export type UserUpdateInput = z.infer<typeof userUpdateSchema>;

export const contactSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  contact_types: z.array(z.enum(CONTACT_TYPES)).min(1, 'Mindestens ein Typ erforderlich'),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  notes: z.string().nullable(),
  external_id: z.string().nullable(),
  external_source: z.string().nullable(),
  created_at: isoTimestamp,
  updated_at: isoTimestamp,
});
export type Contact = z.infer<typeof contactSchema>;

// Beim Anlegen/Updaten dürfen E-Mail/Telefon/Notizen weggelassen werden.
export const contactInputSchema = z.object({
  name: z.string().min(1),
  contact_types: z.array(z.enum(CONTACT_TYPES)).min(1, 'Mindestens ein Typ erforderlich'),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  // CRM-Verknüpfung — wird nur beim Import gesetzt, sonst weglassen.
  external_id: z.string().max(64).nullable().optional(),
  external_source: z.string().max(32).nullable().optional(),
});
export type ContactInput = z.infer<typeof contactInputSchema>;

// Such-Treffer aus dem CRM (vor Import noch in Stromo-Format gemappt).
export const crmContactCandidateSchema = z.object({
  external_id: z.string(),
  external_source: z.string(),
  // ist die Person/Firma schon als Stromo-Kontakt importiert?
  already_imported_id: z.number().int().positive().nullable(),
  display_name: z.string(),
  is_private: z.boolean(),
  company: z.string().nullable(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
});
export type CrmContactCandidate = z.infer<typeof crmContactCandidateSchema>;

export const templatePositionSchema = z.object({
  id: z.number().int().positive(),
  template_id: z.number().int().positive(),
  sort_order: z.number().int().nonnegative(),
  position_number: z.number().int().nonnegative(),
  title: z.string().min(1),
  is_group_header: z.boolean(),
});
export type TemplatePosition = z.infer<typeof templatePositionSchema>;

export const templateSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  description: z.string().nullable(),
  version: z.number().int().nonnegative(),
  is_active: z.boolean(),
  created_at: isoTimestamp,
  updated_at: isoTimestamp,
  positions: z.array(templatePositionSchema).optional(),
});
export type Template = z.infer<typeof templateSchema>;

export const templateInputSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  is_active: z.boolean().optional(),
  positions: z
    .array(
      z.object({
        sort_order: z.number().int().nonnegative(),
        position_number: z.number().int().nonnegative(),
        title: z.string().min(1),
        is_group_header: z.boolean().optional(),
      }),
    )
    .min(1),
});
export type TemplateInput = z.infer<typeof templateInputSchema>;

export const projectSchema = z.object({
  id: z.number().int().positive(),
  order_number: z.string().min(1),
  title: z.string().min(1),
  client_id: z.number().int().positive().nullable(),
  architect_id: z.number().int().positive().nullable(),
  project_manager_id: z.number().int().positive().nullable(),
  site_manager_id: z.number().int().positive().nullable(),
  start_date: dateString.nullable(),
  end_date: dateString.nullable(),
  status: z.enum(PROJECT_STATUS),
  description: z.string().nullable(),
  notes: z.string().nullable(),
  acceptance_planned: dateString.nullable(),
  acceptance_done: z.boolean(),
  is_archived: z.boolean(),
  template_id: z.number().int().positive().nullable(),
  template_version_at_creation: z.number().int().nullable(),
  created_at: isoTimestamp,
  updated_at: isoTimestamp,
});
export type Project = z.infer<typeof projectSchema>;

export const projectInputSchema = z.object({
  order_number: z.string().min(1).max(50),
  title: z.string().min(1),
  client_id: z.number().int().positive().nullable().optional(),
  architect_id: z.number().int().positive().nullable().optional(),
  project_manager_id: z.number().int().positive().nullable().optional(),
  site_manager_id: z.number().int().positive().nullable().optional(),
  start_date: dateString.nullable().optional(),
  end_date: dateString.nullable().optional(),
  status: z.enum(PROJECT_STATUS).optional(),
  description: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  acceptance_planned: dateString.nullable().optional(),
  acceptance_done: z.boolean().optional(),
  is_archived: z.boolean().optional(),
  template_id: z.number().int().positive().nullable().optional(),
});
export type ProjectInput = z.infer<typeof projectInputSchema>;

export const projectSectionSchema = z.object({
  id: z.number().int().positive(),
  project_id: z.number().int().positive(),
  name: z.string().min(1),
  sort_order: z.number().int().nonnegative(),
});
export type ProjectSection = z.infer<typeof projectSectionSchema>;

export const taskSchema = z.object({
  id: z.number().int().positive(),
  section_id: z.number().int().positive(),
  template_position_id: z.number().int().positive().nullable(),
  sort_order: z.number().int().nonnegative(),
  position_number: z.number().int().nonnegative(),
  title: z.string().min(1),
  is_group_header: z.boolean(),
  schedule_start: dateString.nullable(),
  schedule_end: dateString.nullable(),
  internal_start: dateString.nullable(),
  internal_end: dateString.nullable(),
  status: z.enum(TASK_STATUS),
  progress_percent: z.number().int().min(0).max(100),
  site_manager_id: z.number().int().positive().nullable(),
  subcontractor: z.string().nullable(),
  notes: z.string().nullable(),
  completed_at: dateString.nullable(),
});
export type Task = z.infer<typeof taskSchema>;

export const taskInputSchema = taskSchema
  .omit({ id: true, section_id: true })
  .partial()
  .extend({
    title: z.string().min(1).optional(),
  });
export type TaskInput = z.infer<typeof taskInputSchema>;

export const todoSchema = z.object({
  id: z.number().int().positive(),
  project_id: z.number().int().positive(),
  /** NULL = "Allgemein" (keiner Sektion zugeordnet). */
  section_id: z.number().int().positive().nullable(),
  title: z.string().min(1),
  description: z.string().nullable(),
  created_by: z.number().int().positive().nullable(),
  created_at: isoTimestamp,
  completed_at: isoTimestamp.nullable(),
  completed_by: z.number().int().positive().nullable(),
});
export type Todo = z.infer<typeof todoSchema>;

export const todoInputSchema = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(2000).nullable().optional(),
  section_id: z.number().int().positive().nullable().optional(),
});
export type TodoInput = z.infer<typeof todoInputSchema>;

/** `project_id` verschiebt das Todo in ein anderes Projekt. Ohne
 *  gleichzeitiges `section_id` landet es dort unter "Allgemein". */
export const todoUpdateSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  section_id: z.number().int().positive().nullable().optional(),
  project_id: z.number().int().positive().optional(),
});
export type TodoUpdate = z.infer<typeof todoUpdateSchema>;

/** Server-Antwort beim Lesen — enthält angereicherte Namen der User,
 *  damit der Client nicht für jeden Todo den User-Lookup machen muss.
 *  `section_id`/`section_name` sind NULL, wenn die Sektion soft-gelöscht ist. */
export const todoWithUsersSchema = todoSchema.extend({
  section_name: z.string().nullable(),
  created_by_name: z.string().nullable(),
  created_by_abbr: z.string().nullable(),
  completed_by_name: z.string().nullable(),
  completed_by_abbr: z.string().nullable(),
});
export type TodoWithUsers = z.infer<typeof todoWithUsersSchema>;

export const auditLogSchema = z.object({
  id: z.number().int().positive(),
  user_id: z.number().int().positive().nullable(),
  entity_type: z.enum(AUDIT_ENTITIES),
  entity_id: z.number().int().positive(),
  action: z.enum(AUDIT_ACTIONS),
  field_name: z.string().nullable(),
  old_value: z.string().nullable(),
  new_value: z.string().nullable(),
  created_at: isoTimestamp,
});
export type AuditLog = z.infer<typeof auditLogSchema>;

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const authResponseSchema = z.object({
  token: z.string(),
  user: userSchema,
});
export type AuthResponse = z.infer<typeof authResponseSchema>;
